import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const EVENT_ISO = "2026-08-07T20:00:00";

function formatUnitValue(unitId, value) {
  if (unitId === "days") {
    return value >= 100 ? String(value) : String(value).padStart(2, "0");
  }
  return String(value).padStart(2, "0");
}

function Countdown() {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [eventStarted, setEventStarted] = useState(false);

  useEffect(() => {
    const eventDate = new Date(EVENT_ISO).getTime();

    const updateCountdown = () => {
      const now = Date.now();
      const distance = eventDate - now;

      if (distance < 0) {
        setEventStarted(true);
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, []);

  const timeUnits = [
    { id: "days", value: timeLeft.days, label: t("countdown.days") },
    { id: "hours", value: timeLeft.hours, label: t("countdown.hours") },
    { id: "minutes", value: timeLeft.minutes, label: t("countdown.minutes") },
    { id: "seconds", value: timeLeft.seconds, label: t("countdown.seconds") },
  ];

  return (
    <section
      className="pt-12 pb-12 bg-white dark:bg-slate-800 transition-colors relative z-10"
      id="countdown"
      aria-labelledby="countdown-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h2
            id="countdown-heading"
            className="text-4xl md:text-5xl mb-5 text-primary dark:text-primary-light relative inline-block font-semibold tracking-tight"
          >
            {t("countdown.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-16 h-0.5 bg-primary dark:bg-primary-light rounded-full"
              aria-hidden
            />
          </h2>
          <p className="text-pretty text-slate-600 dark:text-slate-400 mt-5 text-lg max-w-3xl mx-auto leading-relaxed">
            {t("countdown.description")}
          </p>
        </header>

        <time dateTime={EVENT_ISO} className="sr-only">
          {t("countdown.sr_only_target")}
        </time>

        {eventStarted ? (
          <div className="max-w-xl mx-auto text-center" role="status">
            <div
              className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/12 dark:bg-emerald-400/10"
              aria-hidden
            >
              <i className="fas fa-check-circle text-2xl text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-xl md:text-2xl font-semibold text-slate-900 dark:text-slate-100 leading-snug">
              {t("countdown.event_started")}
            </p>
          </div>
        ) : (
          <div
            className="max-w-3xl lg:max-w-5xl mx-auto"
            role="group"
            aria-labelledby="countdown-heading"
          >
            <ul className="grid grid-cols-1 lg:grid-cols-4 list-none p-0 m-0 lg:gap-2">
              {timeUnits.map((unit, index) => (
                <li
                  key={unit.id}
                  className={[
                    "flex flex-row items-center justify-between gap-4 py-4 px-1 sm:px-2",
                    "border-b border-slate-200/80 dark:border-slate-600/50 last:border-b-0",
                    "lg:flex-col lg:justify-center lg:items-center lg:gap-0.5 lg:py-5 lg:px-3 lg:border-b-0 lg:relative",
                    index < timeUnits.length - 1
                      ? "lg:before:content-[''] lg:before:absolute lg:before:right-0 lg:before:top-[15%] lg:before:h-[70%] lg:before:w-px lg:before:bg-gradient-to-b lg:before:from-transparent lg:before:via-slate-300/60 lg:before:to-transparent dark:lg:before:via-slate-500/45"
                      : "",
                  ].join(" ")}
                >
                  <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 uppercase tracking-[0.12em] font-semibold lg:order-2 lg:mt-1">
                    {unit.label}
                  </span>
                  <span className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold text-primary dark:text-primary-light tabular-nums tracking-tight lg:order-1">
                    {formatUnitValue(unit.id, unit.value)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

export default Countdown;
